/*
 * Case Pair — one-tap second case from inside the cart.
 *
 * Defined as a custom element rather than a document-level listener because
 * both cart surfaces replace their innerHTML wholesale on every cart update.
 * connectedCallback re-runs on each of those re-renders; a <script> inserted
 * via innerHTML would not execute at all.
 *
 * Originals are selected in the cart with their own phone model. A custom
 * second case uses the customizer; the first case stays safely in the cart.
 */
(function () {
  'use strict';

  function parseMap(button) {
    try {
      return JSON.parse(button.dataset.variants || '{}');
    } catch (error) {
      return {};
    }
  }

  class CasePairOffer extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('[data-case-pair-model]');
      this.statusEl = this.querySelector('[data-case-pair-status]');
      this.tiles = Array.prototype.slice.call(this.querySelectorAll('[data-case-pair-add]'));

      this.onSelect = this.syncAvailability.bind(this);
      this.onClick = this.handleClick.bind(this);

      if (this.select) this.select.addEventListener('change', this.onSelect);
      this.tiles.forEach(
        function (tile) {
          tile.addEventListener('click', this.onClick);
        }.bind(this)
      );

      this.syncAvailability();
      window.RoboPixelPairJourney?.paintCouponCopy?.();
      this.custom = this.querySelector('[data-case-pair-custom]');
      this.onCustom = function () {
        // Cart state owns eligibility and pricing. Optional session provenance
        // must never block a normal link (including when storage is disabled).
        if (window.RoboPixelPairJourney) window.RoboPixelPairJourney.prepareCustom(this.dataset.firstKey);
      }.bind(this);
      if (this.custom) this.custom.addEventListener('click', this.onCustom);
    }

    disconnectedCallback() {
      if (this.select) this.select.removeEventListener('change', this.onSelect);
      if (this.custom) this.custom.removeEventListener('click', this.onCustom);
      this.tiles.forEach(
        function (tile) {
          tile.removeEventListener('click', this.onClick);
        }.bind(this)
      );
    }

    get model() {
      return this.select ? this.select.value : this.dataset.selected;
    }

    /*
     * Two jobs on every model change.
     *
     * Availability: inventory policy on every case is DENY, so a design can
     * genuinely run out in one phone model while remaining available in others.
     * Hiding the dead tile is calmer than rendering one that rejects the tap.
     *
     * Artwork: these cases have no variant image, so the thumbnail has to be
     * repointed at the picked model's own photography. Without this the row
     * would keep showing the iPhone 17 Pro Max render for every model.
     */
    syncAvailability() {
      if (this.adding) return;
      var model = this.model;
      if (this.statusEl) this.statusEl.textContent = '';

      this.tiles.forEach(function (tile) {
        var entry = parseMap(tile)[model];
        tile.hidden = !!model && !entry;
        var cell = tile.closest('.rp-pair__cell');
        if (cell) cell.hidden = tile.hidden;
        tile.disabled = !entry;
        if (!entry) return;

        var img = tile.querySelector('.rp-pair__img');
        if (img && entry[1] && img.getAttribute('src') !== entry[1]) {
          img.setAttribute('src', entry[1]);
        }
        if (img) img.alt = tile.dataset.title + ' — ' + model;
      });
    }

    setStatus(message) {
      if (this.statusEl) this.statusEl.textContent = message || '';
    }

    handleClick(event) {
      var tile = event.currentTarget;
      if (tile.disabled || this.adding) return;
      event.preventDefault();

      var entry = parseMap(tile)[this.model];
      if (!entry) {
        this.setStatus(this.dataset.i18nUnavailable);
        return;
      }
      var variantId = entry[0];

      var context = this.dataset.context;
      var body = { id: variantId, quantity: 1 };
      // Each selection adds just this independently chosen model/design.
      // An explicit first-case line is retained for pairing provenance only.
      if (this.dataset.firstKey) body.properties = { '_Case_Pair_First': this.dataset.firstKey };

      if (context === 'drawer') {
        body.sections = 'cart-drawer,cart-icon-bubble';
        body.sections_url = window.location.pathname;
      } else {
        var pageSections = this.pageSectionIds();
        if (pageSections.length) {
          body.sections = pageSections.join(',');
          body.sections_url = window.location.pathname;
        }
      }

      this.tiles.forEach(function (other) {
        other.disabled = true;
      });
      this.adding = true;
      if (this.select) this.select.disabled = true;
      tile.classList.add('is-adding');
      this.setStatus(this.dataset.i18nAdding);

      // Fail closed if the pricing guard did not load. An unguarded add can
      // charge full price after another tab removes the qualifying first case.
      Promise.resolve().then(function () {
        if (!window.RoboPixelPairJourney || !window.RoboPixelPairJourney.addChecked) throw new Error(this.dataset.i18nError);
        return window.RoboPixelPairJourney.addChecked(body, true, Number(this.dataset.incrementCents));
      }.bind(this))
        .then(
          function (data) {
            if (window.RoboPixelPairJourney) window.RoboPixelPairJourney.cancel();
            var announcement = this.dataset.i18nAdded + ' — ' + tile.dataset.title;

            if (context === 'drawer') {
              var drawer = document.querySelector('cart-drawer');
              if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
                // renderContents() owns window.__cartRenderInProgress, which
                // suppresses any competing onCartUpdate() fetch mid-render.
                drawer.renderContents(data);
                this.refreshPairState();
                this.announce(announcement);
                return;
              }
            } else if (data.sections && this.renderPage(data.sections)) {
              this.refreshPairState();
              this.announce(announcement);
              return;
            }
            window.location.reload();
          }.bind(this)
        )
        .catch(
          function (error) {
            this.adding = false;
            if (this.select) this.select.disabled = false;
            tile.classList.remove('is-adding');
            this.syncAvailability();
            this.setStatus(error.message || this.dataset.i18nError);
            this.announce(error.message || this.dataset.i18nError);
          }.bind(this)
        );
    }

    /*
     * The sections Shopify bundles into the cart/add.js response are rendered
     * BEFORE automatic discounts are recalculated. Verified on this store: after
     * the add that completes a pair, cart.js already reports the line-level
     * "The Case Pair" allocation and a €54,95 total, yet the footer section in
     * that same response still renders the not-yet-discounted state. A separate
     * section render a moment later returns the applied state correctly.
     *
     * This module is entirely about discount state, so it re-reads itself from
     * an authoritative section render and swaps only its own slot. The line item
     * from the first render still appears instantly; this just settles the
     * progress bar and the confirmation line a beat later.
     */
    refreshPairState() {
      var drawer = this.dataset.context === 'drawer';
      var footer = document.getElementById('main-cart-footer');
      var section = drawer ? 'cart-drawer' : footer && footer.dataset.id;
      if (!section) return;

      var slot = drawer ? 'cart-drawer .rp-case-pair-status-slot' : '#main-cart-footer .rp-case-pair-status-slot';

      window.setTimeout(function () {
        fetch(window.location.pathname + '?sections=' + encodeURIComponent(section))
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .then(function (data) {
            if (!data || !data[section]) return;
            var incoming = new DOMParser()
              .parseFromString(data[section], 'text/html')
              .querySelector('.rp-case-pair-status-slot');
            var current = document.querySelector(slot);
            if (incoming && current && current.innerHTML !== incoming.innerHTML) {
              current.innerHTML = incoming.innerHTML;
            }
          })
          .catch(function () {
            // The cart itself is already correct; a stale progress bar is not
            // worth surfacing an error over.
          });
      }, 400);
    }

    /*
     * A successful add replaces this whole module, so the confirmation has to
     * be spoken through a live region that survives the swap. Both cart
     * surfaces already ship one for their own quantity updates; writing after
     * the re-render is what makes the change perceivable to a screen reader,
     * which otherwise gets a silent DOM replacement.
     */
    announce(message) {
      var context = this.dataset.context;
      window.setTimeout(function () {
        var region =
          context === 'drawer'
            ? document.getElementById('CartDrawer-LiveRegionText')
            : document.getElementById('cart-live-region-text');
        if (region) region.textContent = message;
      }, 350);
    }

    /*
     * Mirrors the section list cart.js re-renders on the full cart page. The
     * section *name* is read off each element's data-id, because a section
     * rendered through a JSON template carries a generated id rather than its
     * filename.
     */
    pageTargets() {
      return [
        { el: document.getElementById('main-cart-items'), selector: '.js-contents' },
        { el: document.getElementById('main-cart-footer'), selector: '.js-contents' },
        { el: document.getElementById('cart-icon-bubble'), selector: '.shopify-section', name: 'cart-icon-bubble' },
        {
          el: document.getElementById('cart-live-region-text'),
          selector: '.shopify-section',
          name: 'cart-live-region-text',
        },
      ].filter(function (target) {
        return target.el;
      });
    }

    pageSectionIds() {
      return this.pageTargets().map(function (target) {
        return target.name || target.el.dataset.id;
      });
    }

    renderPage(sections) {
      var targets = this.pageTargets();
      if (!targets.length) return false;

      var rendered = false;
      targets.forEach(function (target) {
        var html = sections[target.name || target.el.dataset.id];
        if (!html) return;

        var source = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
        var destination = target.el.querySelector(target.selector);

        if (target.selector === '.shopify-section') {
          if (!source) return;
          target.el.innerHTML = source.innerHTML;
          rendered = true;
          return;
        }
        if (!source || !destination) return;
        destination.innerHTML = source.innerHTML;
        rendered = true;
      });

      return rendered;
    }
  }

  if (!customElements.get('case-pair-offer')) {
    customElements.define('case-pair-offer', CasePairOffer);
  }
})();
